import { Typography } from '@/components/UI'
import { Button } from '@/components/UI/Button'
import { Dialog } from '@/components/UI/Dialog'
import type { QuestionnaireAnswerFormData } from '@/schemas/forms/questionnaire'
import type { QuestionnaireData } from '@/schemas/v2/questionnaire'
import { Input, InputWrapper, MantineProvider, createTheme } from '@mantine/core'
import { memo } from 'react'
import type { CSSProperties } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { QuestionnaireFormField } from './QuestionnaireFormField'

// iOS Safari の自動ズーム防止のため --input-fz を 16px に統一し、
// ラベルも同様に 1rem (16px) に揃える
const dialogFormTheme = createTheme({
  components: {
    Input: Input.extend({
      styles: {
        wrapper: { '--input-fz': '16px' } as CSSProperties,
      },
    }),
    InputWrapper: InputWrapper.extend({
      styles: {
        label: { fontSize: '1rem' },
        error: { marginTop: '8px' },
      },
    }),
  },
})

type Props = {
  questionnaire: QuestionnaireData | null
  isOpen: boolean
  onClose: () => void
  onSubmit: (answers: QuestionnaireAnswerFormData) => Promise<boolean | undefined>
  submitError?: string | null
  isSubmitting?: boolean
}

export const DialogQuestionnaire = memo(
  ({ questionnaire, isOpen, onClose, onSubmit, submitError, isSubmitting = false }: Props) => {
    const { control, handleSubmit, reset } = useForm<QuestionnaireAnswerFormData>()
    const { t } = useTranslation()

    const handleClose = () => {
      if (isSubmitting) return
      reset()
      onClose()
    }

    const handleFormSubmit = async (data: QuestionnaireAnswerFormData) => {
      const success = await onSubmit(data)
      // false のときのみリセットを抑制（void/undefined は成功扱い）
      if (success !== false) {
        reset()
      }
    }

    if (!questionnaire) return null

    const items = questionnaire.items ?? []

    return (
      <Dialog
        selectorState={isOpen}
        closeFunc={handleClose}
        showCloseButton={false}
        closeOnClickOutside={false}
        title={questionnaire.title ?? t('register_questionnaire.title', 'アンケート')}
      >
        {questionnaire.description && (
          <Typography variant={'caption1'} c={'black'} className={'mt-2 whitespace-pre-wrap'}>
            {questionnaire.description}
          </Typography>
        )}
        <MantineProvider theme={dialogFormTheme}>
          <form onSubmit={handleSubmit(handleFormSubmit)} className="mt-4 flex flex-col gap-0">
            {/* スクロール可能なフィールド領域 */}
            <div className="space-y-6 overflow-y-auto max-h-[55vh] pb-2 px-1">
              {items.map((item) => (
                <QuestionnaireFormField key={item.id} item={item} control={control} />
              ))}
              {items.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">
                  {t('register_questionnaire.no_questions', '設問がありません')}
                </p>
              )}
            </div>
            {/* ボタンは常に下部に固定 */}
            <div className="flex flex-col items-center pt-4 border-t border-gray-100 mt-4 gap-4">
              {submitError && <p className="text-xs text-red-600 text-center">{submitError}</p>}
              <Button
                label={t('register_questionnaire.submit', '回答する')}
                type="submit"
                disabled={isSubmitting}
                fullWidth
              />
            </div>
          </form>
        </MantineProvider>
      </Dialog>
    )
  },
)

DialogQuestionnaire.displayName = 'DialogQuestionnaire'
